'use client';

import { useState } from 'react';
import { Clock, BookOpen, Heart, Bookmark, Play, GraduationCap, Languages, Globe } from 'lucide-react';
import { useSession } from 'next-auth/react';
import { getLanguageInfo } from '@/lib/audio-book-tags';

interface RailBookCardProps {
  book: {
    id: string;
    title: string;
    prompt: string;
    coverImageUrl: string | null;
    pageCount: number;
    totalDuration: number | null;
    language: string;
    isFeatured: boolean;
    isFeaturedSample?: boolean;
    montessoriMode: boolean;
    ageBucket: string | null;
    progress?: number;
    bilingualMode?: boolean;
    isLearningContent?: boolean;
    sourceLanguage?: string;
    targetLanguage?: string;
  };
  animationDelay?: number;
  isFavorited?: boolean;
  isInWatchlist?: boolean;
  onFavoriteToggle?: (bookId: string, isAdding: boolean) => Promise<void>;
  onWatchlistToggle?: (bookId: string, isAdding: boolean) => Promise<void>;
}

export default function RailBookCard({
  book,
  animationDelay = 0,
  isFavorited = false,
  isInWatchlist = false,
  onFavoriteToggle,
  onWatchlistToggle,
}: RailBookCardProps) {
  const { data: session } = useSession();
  const [favoriteState, setFavoriteState] = useState(isFavorited);
  const [watchlistState, setWatchlistState] = useState(isInWatchlist);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  // Determine if bilingual/learning book
  const isBilingual = book.bilingualMode || (book.sourceLanguage && book.targetLanguage);
  const isLearning = book.isLearningContent;
  
  // Get language info for display
  const sourceLanguageInfo = isBilingual 
    ? getLanguageInfo(book.sourceLanguage || book.language)
    : getLanguageInfo(book.language);
  const targetLanguageInfo = isBilingual && book.targetLanguage
    ? getLanguageInfo(book.targetLanguage)
    : null;

  const formatDuration = (seconds: number | null) => {
    if (!seconds || seconds === 0) return 'Unknown';
    
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    
    // Show seconds only if under 1 minute
    if (mins === 0) {
      return `${secs}s`;
    }
    
    // Show MM:SS for books under 5 minutes
    if (mins < 5 && secs > 0) {
      return `${mins}:${secs.toString().padStart(2, '0')}`;
    }
    
    // Show minutes only for longer books
    return `${mins} min`;
  };

  async function handleFavoriteClick(e: React.MouseEvent) {
    e.preventDefault();
    e.stopPropagation();

    if (!session || isProcessing) return;

    setIsProcessing(true);
    const newState = !favoriteState;

    try {
      if (onFavoriteToggle) {
        await onFavoriteToggle(book.id, newState);
      } else {
        if (newState) {
          const res = await fetch('/api/favorites', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ bookId: book.id }),
          });
          if (!res.ok) throw new Error('Failed to add to favorites');
        } else {
          const res = await fetch(`/api/favorites?bookId=${book.id}`, {
            method: 'DELETE',
          });
          if (!res.ok) throw new Error('Failed to remove from favorites');
        }
      }
      setFavoriteState(newState);
    } catch (error) {
      console.error('Favorite toggle error:', error);
    } finally {
      setIsProcessing(false);
    }
  }

  async function handleWatchlistClick(e: React.MouseEvent) {
    e.preventDefault();
    e.stopPropagation();

    if (!session || isProcessing) return;

    setIsProcessing(true);
    const newState = !watchlistState;

    try {
      if (onWatchlistToggle) {
        await onWatchlistToggle(book.id, newState);
      } else {
        if (newState) {
          const res = await fetch('/api/watchlist', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ bookId: book.id }),
          });
          if (!res.ok) throw new Error('Failed to add to watchlist');
        } else {
          const res = await fetch(`/api/watchlist?bookId=${book.id}`, {
            method: 'DELETE',
          });
          if (!res.ok) throw new Error('Failed to remove from watchlist');
        }
      }
      setWatchlistState(newState);
    } catch (error) {
      console.error('Watchlist toggle error:', error);
    } finally {
      setIsProcessing(false);
    }
  }

  return (
    <div
      style={{
        width: '240px',
        borderRadius: '16px',
        overflow: 'hidden',
        background: 'rgba(255, 255, 255, 0.05)',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        transform: isHovered ? 'translateY(-8px) scale(1.05)' : 'translateY(0) scale(1)',
        boxShadow: isHovered
          ? '0 12px 40px rgba(0, 0, 0, 0.4)'
          : '0 4px 12px rgba(0, 0, 0, 0.2)',
        animation: `fadeInUp 0.5s ${animationDelay}ms both`,
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Cover Image */}
      <div
        style={{
          position: 'relative',
          width: '100%',
          height: '320px',
          background: book.coverImageUrl
            ? `url(${book.coverImageUrl}) center/cover`
            : 'linear-gradient(135deg, #667eea, #764ba2)',
          overflow: 'hidden',
        }}
      >
        {/* Progress Bar (if in progress) */}
        {book.progress !== undefined && book.progress > 0 && book.progress < 100 && (
          <div
            style={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: '4px',
              background: 'rgba(0, 0, 0, 0.3)',
            }}
          >
            <div
              style={{
                height: '100%',
                width: `${book.progress}%`,
                background: 'linear-gradient(90deg, #4ade80, #22c55e)',
                transition: 'width 0.3s',
              }}
            />
          </div>
        )}

        {/* Action Buttons (hover) */}
        {session && isHovered && (
          <div
            style={{
              position: 'absolute',
              top: '0.5rem',
              right: '0.5rem',
              display: 'flex',
              gap: '0.5rem',
            }}
          >
            <button
              onClick={handleFavoriteClick}
              disabled={isProcessing}
              style={{
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                background: 'rgba(0, 0, 0, 0.7)',
                backdropFilter: 'blur(10px)',
                border: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: isProcessing ? 'not-allowed' : 'pointer',
                opacity: isProcessing ? 0.6 : 1,
              }}
              aria-label={favoriteState ? 'Remove from favorites' : 'Add to favorites'}
            >
              <Heart size={16} fill={favoriteState ? '#ef4444' : 'none'} color={favoriteState ? '#ef4444' : '#fff'} />
            </button>

            <button
              onClick={handleWatchlistClick}
              disabled={isProcessing}
              style={{
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                background: 'rgba(0, 0, 0, 0.7)',
                backdropFilter: 'blur(10px)',
                border: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: isProcessing ? 'not-allowed' : 'pointer',
                opacity: isProcessing ? 0.6 : 1,
              }}
              aria-label={watchlistState ? 'Remove from watchlist' : 'Add to watchlist'}
            >
              <Bookmark size={16} fill={watchlistState ? '#3b82f6' : 'none'} color={watchlistState ? '#3b82f6' : '#fff'} />
            </button>
          </div>
        )}

        {/* Badges - Compact Design */}
        <div
          style={{
            position: 'absolute',
            top: '0.5rem',
            left: '0.5rem',
            display: 'flex',
            flexDirection: 'column',
            gap: '0.25rem',
            maxWidth: 'calc(100% - 4.5rem)', // Leave space for action buttons
          }}
        >
          {/* Learning Mode Badge - Compact */}
          {(isBilingual || isLearning) && (
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.25rem',
                padding: '0.25rem 0.5rem',
                background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 50%, #3b82f6 100%)',
                borderRadius: '6px',
                fontSize: '0.625rem',
                fontWeight: 700,
                color: 'white',
                boxShadow: '0 2px 6px rgba(139, 92, 246, 0.3)',
                textTransform: 'uppercase',
                letterSpacing: '0.02em',
                border: '1px solid rgba(255, 255, 255, 0.25)',
                width: 'fit-content',
              }}
            >
              <GraduationCap size={11} strokeWidth={2.5} />
              <span>Learn</span>
            </div>
          )}

          {/* Language Badges - Compact with Abbreviations */}
          {isBilingual && targetLanguageInfo ? (
            /* Bilingual: EN → ES format */
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.3rem',
                padding: '0.25rem 0.5rem',
                background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.95), rgba(249, 117, 131, 0.95))',
                borderRadius: '6px',
                fontSize: '0.625rem',
                fontWeight: 700,
                color: 'white',
                boxShadow: '0 2px 6px rgba(0, 0, 0, 0.2)',
                border: '1px solid rgba(255, 255, 255, 0.25)',
                width: 'fit-content',
              }}
            >
              <span>{sourceLanguageInfo.code}</span>
              <Languages size={10} strokeWidth={2.5} style={{ opacity: 0.9 }} />
              <span>{targetLanguageInfo.code}</span>
            </div>
          ) : (
            /* Single Language Badge */
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.25rem',
                padding: '0.25rem 0.5rem',
                background: sourceLanguageInfo.gradient,
                borderRadius: '6px',
                fontSize: '0.625rem',
                fontWeight: 700,
                color: 'white',
                boxShadow: '0 2px 6px rgba(0, 0, 0, 0.2)',
                border: '1px solid rgba(255, 255, 255, 0.25)',
                width: 'fit-content',
              }}
            >
              <Globe size={10} strokeWidth={2.5} />
              <span>{sourceLanguageInfo.name}</span>
            </div>
          )}

          {/* Free Badge - Compact */}
          {book.isFeaturedSample && (
            <div
              style={{
                display: 'inline-flex',
                padding: '0.25rem 0.5rem',
                background: 'linear-gradient(135deg, #10b981, #059669)',
                borderRadius: '6px',
                fontSize: '0.625rem',
                fontWeight: 700,
                color: 'white',
                boxShadow: '0 2px 6px rgba(0, 0, 0, 0.2)',
                border: '1px solid rgba(255, 255, 255, 0.25)',
                width: 'fit-content',
              }}
            >
              FREE
            </div>
          )}
        </div>

        {/* Play Icon (center, on hover) */}
        {isHovered && (
          <div
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '56px',
              height: '56px',
              borderRadius: '50%',
              background: 'rgba(255, 255, 255, 0.95)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 16px rgba(0, 0, 0, 0.3)',
              animation: 'scaleIn 0.2s',
            }}
          >
            <Play size={28} fill="black" color="black" style={{ marginLeft: '4px' }} />
          </div>
        )}
      </div>

      {/* Book Info */}
      <div style={{ padding: '0.75rem' }}>
        <h3
          style={{
            fontSize: '0.95rem',
            fontWeight: 700,
            margin: '0 0 0.5rem 0',
            color: 'white',
            lineHeight: 1.3,
            height: '2.6em',
            overflow: 'hidden',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
          }}
        >
          {book.title}
        </h3>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            fontSize: '0.8rem',
            color: 'rgba(255, 255, 255, 0.7)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
            <BookOpen size={14} />
            <span>{book.pageCount}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
            <Clock size={14} />
            <span>{formatDuration(book.totalDuration)}</span>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes scaleIn {
          from {
            transform: translate(-50%, -50%) scale(0.8);
            opacity: 0;
          }
          to {
            transform: translate(-50%, -50%) scale(1);
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
}

